import { ActionCreator } from '@sendoutcards/core'
import {
  ContactFragment,
  DetailedContactFragment,
  DetailedGroupFragment,
  GroupFragment,
  MinimalContactFragment,
} from 'src/graphql'
import { Group } from 'src/contacts/contactTypes'
import { Filter } from 'src/app/types'
import { ContactModals } from '../reducers/contacts'
import Result from 'src/utils/Result'

export const selectAll = ActionCreator(
  'SELECT_ALL',
  ({
    arr,
    dataType,
  }:
    | { arr: MinimalContactFragment[]; dataType: 'selectedContacts' }
    | { arr: { id: string }[]; dataType: 'selectedGroups' }) => ({
    arr,
    dataType,
  }),
)

export const setActiveAlert = ActionCreator(
  'SET_ACTIVE_ALERT',
  (alertType: 'EXPORTING' | null) => ({ alertType }),
)

export const editContact = ActionCreator(
  'EDIT_CONTACT',
  (contact: ContactFragment) => ({
    contact,
  }),
)

export const createdNewGroup = ActionCreator(
  'CREATED_NEW_GROUP',
  (group: DetailedGroupFragment) => ({ group }),
)

export const resetActiveGroup = ActionCreator('RESET_ACTIVE_GROUP')

export const loadedContactsError = ActionCreator(
  'LOADED_CONTACTS_ERROR',
  (error: Error) => ({ error }),
)

export const createdContactError = ActionCreator(
  'CREATED_CONTACT_ERROR',
  (error: Error) => ({ error }),
)

export const selectContact = ActionCreator(
  'SELECT_CONTACT',
  (contact: ContactFragment) => ({ contact }),
)

export const setContactsToLoad = ActionCreator(
  'SET_CONTACTS_TO_LOAD',
  (contacts: string[]) => ({ contacts }),
)

export const savedContactError = ActionCreator(
  'SAVED_CONTACT_ERROR',
  (error: Error) => ({ error }),
)

export const savedContact = ActionCreator(
  'SAVED_CONTACT',
  (contact?: DetailedContactFragment) => ({
    contact,
  }),
)

export const setSearchPredicate = ActionCreator(
  'SET_CONTACTS_SEARCH_PREDICATE',
  (searchPredicate?: string) => ({ searchPredicate }),
)

export const sharedContact = ActionCreator(
  'SHARED_CONTACT',
  (contact: ContactFragment) => ({ contact }),
)

export const loadContacts = ActionCreator('LOAD_CONTACTS')

export const loadContact = ActionCreator('LOAD_CONTACT', (contact: string) => ({
  contact,
}))

export const loadedContact = ActionCreator(
  'LOADED_CONTACT',
  (contact?: DetailedContactFragment) => ({ contact }),
)

export const sharedContactUserError = ActionCreator(
  'SHARED_CONTACT_USER_ERROR',
  (username: string) => ({ username }),
)

export const selectGroupMember = ActionCreator(
  'SELECT_GROUP_MEMBER',
  (groupMember: ContactFragment) => ({ groupMember }),
)

export const clearSharedLists = ActionCreator('CLEAR_SHARED_LISTS')

export const allGroupsLoaded = ActionCreator(
  'ALL_GROUPS_LOADED',
  (groups: GroupFragment[]) => ({ groups }),
)

export const deletedContact = ActionCreator(
  'DELETED_CONTACT',
  (contact: ContactFragment) => ({ contact }),
)

export const openGroupMemberModal = ActionCreator('OPEN_GROUP_MEMBER_MODAL')

export const selectAllMembers = ActionCreator(
  'SELECT_ALL_MEMBERS',
  (members: ContactFragment[]) => ({ members }),
)

export const deletedGroup = ActionCreator('DELETED_GROUP', (group: Group) => ({
  group,
}))

export const setActiveGroup = ActionCreator(
  'SET_ACTIVE_GROUP',
  (group?: DetailedGroupFragment) => ({ group }),
)

export const deletedContactError = ActionCreator(
  'DELETED_CONTACT_ERROR',
  (contact: ContactFragment) => ({ contact }),
)

export const cancelEditContact = ActionCreator('CANCEL_EDIT_CONTACT')

export const handleEnterOnSearch = ActionCreator(
  'HANDLE_ENTER_ON_SEARCH',
  (searchTerm?: string) => ({ searchTerm }),
)

export const loadedContacts = ActionCreator(
  'LOADED_CONTACTS',
  (
    contacts?: ContactFragment[],
    count: number = 0,
    contactIds?: MinimalContactFragment[],
  ) => ({
    contacts,
    count,
    contactIds,
  }),
)

export const clearDeletedList = ActionCreator('CLEAR_DELETED_LIST')

export const deletedGroupError = ActionCreator(
  'DELETED_GROUP_ERROR',
  (group: Group) => ({ group }),
)

export const exportContacts = ActionCreator(
  'CONTACTS_EXPORT',
  (email: string) => ({ email }),
)

export const exportedContacts = ActionCreator('CONTACTS_EXPORTED')

export const filtersChanged = ActionCreator(
  'FILTERS_CHANGED',
  (filters: Filter[]) => ({ filters }),
)

export const selectGroup = ActionCreator('SELECT_GROUP', (group: Group) => ({
  group,
}))

export const setActiveContact = ActionCreator(
  'SET_ACTIVE_CONTACT',
  (contact?: DetailedContactFragment) => ({ contact }),
)

export const changeShareUserBool = ActionCreator(
  'CHANGE_SHARE_USER_BOOL',
  (bool: boolean) => ({ bool }),
)

export const toggleModal = ActionCreator(
  'TOGGLE_MODAL',
  (modalType: keyof ContactModals) => ({
    modalType,
  }),
)

export const setCurrentPage = ActionCreator(
  'SET_CONTACTS_CURRENT_PAGE',
  (currentPage: number) => ({ currentPage }),
)

export const changeContext = ActionCreator('CHANGE_CONTEXT')

export const changeOrderStatus = ActionCreator(
  'CHANGE_ORDER_STATUS',
  (status: boolean) => ({ status }),
)

export const loadGroup = ActionCreator(
  'LOAD_GROUP',
  (group: string, setAsActive?: boolean) => ({ group, setAsActive }),
)

export const loadedGroup = ActionCreator(
  'LOADED_GROUP',
  (group?: DetailedGroupFragment) => ({
    group,
  }),
)

export const loadedGroups = ActionCreator(
  'LOADED_GROUPS',
  (groups?: Group[], groupsCount: number = 0, groupIds?: { id: string }[]) => ({
    groups,
    groupsCount,
    groupIds,
  }),
)

export const updateGroup = ActionCreator('UPDATE_GROUP', (group: Group) => ({
  group,
}))

export const updatedGroup = ActionCreator(
  'UPDATED_GROUP',
  (result: Result<DetailedGroupFragment>) => ({
    result,
  }),
)

export const editGroup = ActionCreator('EDIT_GROUP', (group: Group | null) => ({
  group,
}))

export const closeGroupMemberModal = ActionCreator('CLOSE_GROUP_MEMBER_MODAL')

export const sharedContactError = ActionCreator(
  'SHARED_CONTACT_ERROR',
  (contact: ContactFragment) => ({ contact }),
)

export const removedGroupMember = ActionCreator('REMOVED_GROUP_MEMBER')

export const addedToGroup = ActionCreator('ADDED_TO_GROUP')

export const createdContact = ActionCreator(
  'CREATED_CONTACT',
  (contact: DetailedContactFragment) => ({ contact }),
)

export const clearSelectedList = ActionCreator('CLEAR_SELECTED_LIST')
